import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import { Card, Grid, Skeleton, Chip, Divider, CardMedia, Box, Typography } from "@mui/material";
import axios, { baseURL } from "utils/api";
import { useParams } from "react-router-dom";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import PropTypes from "prop-types";

const VideoCard = React.memo(
  ({ question, answer, index, type }) => {
    const [videoLoading, setVideoLoading] = useState(true);
    const [videoError, setVideoError] = useState(false);
    const hasLoadedRef = useRef(false);
    const videoRef = useRef(null);

    // Video events handlers - stable references
    const handleVideoError = useCallback(() => {
      if (!hasLoadedRef.current) {
        setVideoError(true);
        setVideoLoading(false);
        hasLoadedRef.current = true;
      }
    }, []);

    const handleVideoCanPlay = useCallback(() => {
      if (!hasLoadedRef.current) {
        setVideoLoading(false);
        setVideoError(false);
        hasLoadedRef.current = true;
      }
    }, []);

    return (
      <Card
        sx={{
          p: 3,
          height: "100%",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          borderRadius: "16px",
          border: "1px solid rgba(0, 0, 0, 0.08)",
          "&:hover": {
            transform: "translateY(-6px)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
            borderColor: "rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <ArgonBox>
          {/* Header with Question Number */}
          <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Chip
              icon={<QuestionAnswerIcon />}
              label={`Question ${index + 1}`}
              color="info"
              variant="outlined"
              size="small"
              sx={{
                borderRadius: "8px",
                fontWeight: 600,
              }}
            />
            <Chip
              label={type}
              color={type === "INTRODUCTION" ? "success" : "primary"}
              size="small"
              sx={{
                borderRadius: "8px",
                fontWeight: 600,
              }}
            />
          </ArgonBox>

          {/* Question */}
          <ArgonBox mb={3}>
            <ArgonTypography
              variant="caption"
              color="text"
              fontWeight="bold"
              mb={1.5}
              sx={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
            >
              Question:
            </ArgonTypography>
            <ArgonTypography
              variant="body2"
              color="dark"
              fontWeight="medium"
              sx={{
                lineHeight: 1.6,
                fontSize: "0.95rem",
              }}
            >
              {typeof question === "object" && question?.question
                ? question.question
                : typeof question === "string"
                ? question
                : "No question available"}
            </ArgonTypography>
          </ArgonBox>

          <Divider sx={{ my: 3, borderColor: "rgba(0, 0, 0, 0.08)" }} />

          {/* Video Player */}
          <ArgonBox>
            <ArgonBox display="flex" alignItems="center" gap={1} mb={2}>
              <PlayCircleOutlineIcon fontSize="small" color="success" />
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                sx={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
              >
                Video Answer:
              </ArgonTypography>
            </ArgonBox>

            {answer ? (
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  backgroundColor: "#000",
                }}
              >
                {videoLoading && !videoError && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 1,
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      width={60}
                      height={60}
                      sx={{ borderRadius: "8px" }}
                    />
                  </Box>
                )}
                <CardMedia
                  component="video"
                  controls
                  src={answer}
                  preload="metadata"
                  sx={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "320px",
                    display: "block",
                    borderRadius: "12px",
                    opacity: videoLoading ? 0.3 : 1,
                    transition: "opacity 0.3s ease",
                  }}
                  onError={handleVideoError}
                  onCanPlay={handleVideoCanPlay}
                />
                {videoError && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      textAlign: "center",
                      color: "white",
                      zIndex: 2,
                    }}
                  >
                    <PlayCircleOutlineIcon sx={{ fontSize: 48, mb: 1, opacity: 0.7 }} />
                    <ArgonTypography variant="body2" sx={{ color: "white" }}>
                      Video failed to load
                    </ArgonTypography>
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{
                  height: "240px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "12px",
                  border: "2px dashed #e9ecef",
                  borderStyle: "dashed",
                }}
              >
                <PlayCircleOutlineIcon sx={{ fontSize: 48, color: "#adb5bd", mb: 1 }} />
                <Typography variant="body2" color="textSecondary" align="center">
                  No video available
                </Typography>
              </Box>
            )}
          </ArgonBox>
        </ArgonBox>
      </Card>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    return (
      prevProps.answer === nextProps.answer &&
      prevProps.index === nextProps.index &&
      prevProps.type === nextProps.type &&
      JSON.stringify(prevProps.question) === JSON.stringify(nextProps.question)
    );
  }
);

VideoCard.displayName = "VideoCard";

VideoCard.propTypes = {
  question: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      question: PropTypes.string,
    }),
  ]).isRequired,
  answer: PropTypes.string,
  index: PropTypes.number.isRequired,
  type: PropTypes.oneOf(["INTRODUCTION", "INTERVIEW"]).isRequired,
};

function IntroductionDetails() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [introductionData, setIntroductionData] = useState([]);
  const isMountedRef = useRef(true);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;

    if (hasFetchedRef.current) return;

    let isSubscribed = true;

    const fetchIntroductionData = async () => {
      if (!id || !isSubscribed) return;

      hasFetchedRef.current = true;

      try {
        setLoading(true);
        const url = `${baseURL}/que-and-ans?tutorId=${id}&type=INTRODUCTION`;
        const response = await axios.get(url);

        if (!isSubscribed || !isMountedRef.current) return;

        let data = response.data?.data || [];

        const processedData = await Promise.all(
          data.map(async (item) => {
            if (!isSubscribed) return item;

            let question = item.question;
            if (typeof question === "string" && /^[a-f\d]{24}$/i.test(question)) {
              try {
                const qRes = await axios.get(`${baseURL}/questions?id=${question}`);
                if (isSubscribed) {
                  question = qRes.data?.data;
                }
              } catch (e) {
                console.error("Error fetching question:", e);
              }
            }
            return { ...item, question };
          })
        );

        if (isSubscribed && isMountedRef.current) {
          setIntroductionData(processedData);
        }
      } catch (error) {
        if (isSubscribed && isMountedRef.current) {
          console.error("Error fetching introduction data:", error);
          setIntroductionData([]);
        }
      } finally {
        if (isSubscribed && isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchIntroductionData();

    return () => {
      isSubscribed = false;
      isMountedRef.current = false;
    };
  }, [id]);

  const SkeletonCard = () => (
    <Card sx={{ p: 3, height: "100%" }}>
      <Skeleton variant="rectangular" height={20} width="40%" sx={{ mb: 2 }} />
      <Skeleton variant="text" height={30} sx={{ mb: 1 }} />
      <Skeleton variant="text" height={20} width="80%" sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" height={250} sx={{ borderRadius: "8px" }} />
    </Card>
  );

  if (loading) {
    return (
      <ArgonBox>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} md={6} key={item}>
              <SkeletonCard />
            </Grid>
          ))}
        </Grid>
      </ArgonBox>
    );
  }

  const hasData = introductionData.length > 0;

  if (!hasData) {
    return (
      <ArgonBox>
        <Card sx={{ p: 5 }}>
          <ArgonBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={2}
          >
            <QuestionAnswerIcon sx={{ fontSize: 64, color: "#d0d0d0" }} />
            <ArgonTypography variant="h6" color="text">
              No Introduction Data Available
            </ArgonTypography>
            <ArgonTypography variant="caption" color="text" textAlign="center">
              This tutor hasn&apos;t completed their video introduction yet.
            </ArgonTypography>
          </ArgonBox>
        </Card>
      </ArgonBox>
    );
  }

  return (
    <ArgonBox>
      {/* Video Introduction Section */}
      {introductionData.length > 0 && (
        <ArgonBox mb={4}>
          <ArgonBox display="flex" alignItems="center" gap={1} mb={2}>
            <PlayCircleOutlineIcon color="success" />
            <ArgonTypography variant="h6" fontWeight="bold">
              Video Introduction
            </ArgonTypography>
            <Chip label={`${introductionData.length} Videos`} color="success" size="small" />
          </ArgonBox>

          <Grid container spacing={3}>
            {introductionData.map((item, index) => (
              <Grid item xs={12} md={6} key={`video-${item._id || index}`}>
                <VideoCard
                  question={item.question}
                  answer={item.answer}
                  index={index}
                  type="INTRODUCTION"
                />
              </Grid>
            ))}
          </Grid>
        </ArgonBox>
      )}
    </ArgonBox>
  );
}

export default IntroductionDetails;
